import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DonationCampaign, DonationRecord } from '../../types';
import { getDonationCampaignsDB, confirmDonationSuccessDB, markDonationFailedDB } from '../../services/db';
import { verifyToyyibPayBillStatus } from '../../lib/toyyibpay';
import { isSuperAdmin, hasRolePermission } from '../../utils/formatters';

import { PageHeader } from '../../components/common/PageHeader';
import { CardSkeleton } from '../../components/common/Skeleton';
import { DonationCard } from '../../components/donations/DonationCard';
import { CampaignDetailPage } from './CampaignDetailPage';
import { CampaignEditorPage } from './CampaignEditorPage';
import { DashboardDonationsManager } from '../../components/donations/DashboardDonationsManager';
import { useToast } from '../../components/common/Toast';
import { saveViewScroll, restoreViewScroll, resetScrollToTop } from '../../utils/scrollRestoration';
import {
  Heart,
  ShieldCheck,
  Plus,
  Search,
  TrendingUp,
  Users,
  Award,
  SlidersHorizontal,
  LayoutGrid
} from 'lucide-react';

import { formatCurrencyMYR } from '../../utils/formatters';

interface DonationPublicPageProps {
  onNavigateToDashboard?: () => void;
}

export const DonationPublicPage: React.FC<DonationPublicPageProps> = ({
  onNavigateToDashboard
}) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [campaigns, setCampaigns] = useState<DonationCampaign[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('Semua');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Read campaign ID from URL (e.g. /donations/camp-xyz or /donasi/camp-xyz or ?id=camp-xyz)
  const getInitialCampaignId = (): string => {
    if (typeof window === 'undefined') return '';
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts.length >= 2 && (parts[0] === 'donations' || parts[0] === 'donasi')) {
      return parts[1];
    }
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('id') || searchParams.get('campaign') || '';
  };

  const initialCampaignId = getInitialCampaignId();

  const isManagement = Boolean(
    currentUser &&
    hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'AJK'])
  );

  // Page View Modes: 'list' | 'detail' | 'editor' | 'management'
  // Public page always defaults to 'list' (or 'detail' if deep linked)
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>(initialCampaignId);
  const [viewMode, setViewMode] = useState<'list' | 'detail' | 'editor' | 'management'>(() => {
    if (initialCampaignId) return 'detail';
    return 'list';
  });
  const [editingCampaign, setEditingCampaign] = useState<DonationCampaign | null>(null);

  const loadData = () => {
    const cached = localStorage.getItem('asmm_donation_campaigns_v1');
    if (cached) {
      try {
        setCampaigns(JSON.parse(cached));
        setIsLoading(false);
      } catch { }
    }

    getDonationCampaignsDB()
      .then((data) => {
        setCampaigns(data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    loadData();
    const handleUpdate = () => loadData();
    window.addEventListener('asmm_db_updated', handleUpdate);

    // Handle browser navigation (back/forward or tab click)
    const handlePopState = () => {
      const parts = window.location.pathname.split('/').filter(Boolean);
      if (parts.length >= 2 && (parts[0] === 'donations' || parts[0] === 'donasi')) {
        setSelectedCampaignId(parts[1]);
        setViewMode('detail');
      } else {
        setSelectedCampaignId('');
        setViewMode('list');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    window.addEventListener('popstate', handlePopState);

    // Auto-verify returning ToyyibPay FPX payment
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const statusId = searchParams.get('status_id') || searchParams.get('status');
      const billCode = searchParams.get('billcode') || searchParams.get('billCode') || searchParams.get('bill_code');

      if (billCode) {
        verifyToyyibPayBillStatus(billCode).then((res) => {
          if (res.status === 'Disahkan' || statusId === '1') {
            confirmDonationSuccessDB(billCode).then((confirmed) => {
              if (confirmed.donation) {
                showToast('success', 'Alhamdulillah! Bayaran Infaq FPX anda telah disahkan berjaya.');
                window.history.replaceState({}, document.title, window.location.pathname);
              }
            });
          } else if (res.status === 'Gagal' || statusId === '3') {
            markDonationFailedDB(billCode).then(() => {
              showToast('error', 'Pembayaran sumbangan FPX tidak berjaya atau telah dibatalkan. Sila cuba sekali lagi.');
              window.history.replaceState({}, document.title, window.location.pathname);
            });
          } else if (res.status === 'Menunggu Pengesahan' || statusId === '2') {
            showToast('info', 'Transaksi FPX sedang diproses oleh pihak bank. Sila tunggu pengesahan.');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        });
      }
    }


    return () => {
      window.removeEventListener('asmm_db_updated', handleUpdate);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleOpenDetail = (camp: DonationCampaign) => {
    saveViewScroll('donations_list');
    setSelectedCampaignId(camp.id);
    setViewMode('detail');
    const baseTab = window.location.pathname.startsWith('/donasi') ? 'donasi' : 'donations';
    const targetPath = `/${baseTab}/${camp.id}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
    resetScrollToTop();
  };

  const handleBackToList = () => {
    setSelectedCampaignId('');
    setViewMode('list');
    const baseTab = window.location.pathname.startsWith('/donasi') ? 'donasi' : 'donations';
    const targetPath = `/${baseTab}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
    restoreViewScroll('donations_list');
  };

  const handleOpenEdit = (camp: DonationCampaign) => {
    if (!isManagement) return;
    setEditingCampaign(camp);
    setViewMode('editor');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreateNew = () => {
    if (!isManagement) return;
    setEditingCampaign(null);
    setViewMode('editor');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShare = (camp: DonationCampaign) => {
    const baseTab = typeof window !== 'undefined' && window.location.pathname.startsWith('/donasi') ? 'donasi' : 'donations';
    const url = typeof window !== 'undefined' ? `${window.location.origin}/${baseTab}/${camp.id}` : `https://asmm.pages.dev/donations/${camp.id}`;
    const text = `Sumbang ke tabung *${camp.title}* anjuran Persatuan Seni Silat ASMM Kedah:\n${url}`;
    navigator.clipboard.writeText(text);
    showToast('success', 'Pautan kempen disalin ke papan keratan');
  };

  // If viewing single campaign detail
  if (viewMode === 'detail' && selectedCampaignId) {
    return (
      <CampaignDetailPage
        campaignId={selectedCampaignId}
        onBack={handleBackToList}
        onEdit={(camp) => {
          setEditingCampaign(camp);
          setViewMode('editor');
        }}
      />
    );
  }

  // If viewing campaign creator/editor page
  if (viewMode === 'editor' && isManagement) {
    return (
      <CampaignEditorPage
        campaign={editingCampaign}
        onBack={() => {
          setViewMode('list');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onSaved={() => {
          loadData();
          setViewMode('list');
        }}
      />
    );
  }

  // Filtered campaigns
  const filteredCampaigns = campaigns.filter((c) => {
    if (!isManagement && c.status === 'Draf') return false;

    const matchesCat = selectedCategory === 'Semua' || c.category === selectedCategory;
    const matchesSearch =
      c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.story.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.beneficiaryGelanggang.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCat && matchesSearch;
  });

  const featuredCampaign = campaigns.find((c) => c.isFeatured && c.status === 'Aktif') || campaigns[0];
  const totalRaised = campaigns.reduce((acc, c) => acc + c.currentAmount, 0);
  const totalDonors = campaigns.reduce((acc, c) => acc + c.donorCount, 0);
  const activeCount = campaigns.filter((c) => c.status === 'Aktif').length;

  return (
    <div className="w-full space-y-8 min-h-[80vh] bg-white text-slate-900 pb-12">
      {/* 1. Modern Page Hero Header (No Clunky Admin Boxes) */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-2">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-100 text-red-700 text-xs font-bold shadow-2xs">
            <ShieldCheck className="w-4 h-4 text-red-600" />
            <span>Tabung Kebajikan & Pembangunan PSSASMM</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Infaq Pembangunan Ummah & Warisan
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 max-w-xl leading-relaxed">
            Salurkan infaq mulia terus ke akaun rasmi persatuan. Setiap sumbangan diaudit secara telus melalui lejar terbuka.
          </p>
        </div>

        {isManagement && (
          <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
            <button
              type="button"
              onClick={() => {
                if (onNavigateToDashboard) {
                  onNavigateToDashboard();
                } else {
                  setViewMode(viewMode === 'management' ? 'list' : 'management');
                }
              }}
              className={`px-4 py-2.5 font-bold text-xs rounded-full transition-all border flex items-center gap-1.5 cursor-pointer shadow-xs ${viewMode === 'management'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200/80'
                }`}
            >
              {onNavigateToDashboard ? (
                <>
                  <SlidersHorizontal className="w-3.5 h-3.5 text-red-600" />
                  <span>Dashboard Tabung</span>
                </>
              ) : viewMode === 'management' ? (
                <>
                  <LayoutGrid className="w-3.5 h-3.5" />
                  <span>Lihat Direktori Tabung</span>
                </>
              ) : (
                <>
                  <SlidersHorizontal className="w-3.5 h-3.5 text-red-600" />
                  <span>Urus Infaq (Live)</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleCreateNew}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-full transition-all shadow-md shadow-red-600/20 flex items-center gap-2 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Cipta Kempen Baru</span>
            </button>
          </div>
        )}
      </div>

      {/* If Management View is Selected */}
      {viewMode === 'management' && isManagement ? (
        <DashboardDonationsManager />
      ) : (
        <>
          {/* 2. Executive Floating Metric Bar (GoFundMe Aesthetic) */}
          <div className="bg-white rounded-3xl sm:rounded-full py-4 px-6 sm:px-10 shadow-sm border border-slate-100 flex flex-col sm:flex-row items-center justify-around gap-4 sm:gap-8 max-w-3xl mx-auto my-2">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-black text-slate-900">{formatCurrencyMYR(totalRaised)}</div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">Jumlah Terkumpul</div>
            </div>
            <div className="hidden sm:block h-8 w-px bg-slate-100" />
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-black text-slate-900">{totalDonors} Orang</div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">Penderma Mulia</div>
            </div>
            <div className="hidden sm:block h-8 w-px bg-slate-100" />
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-black text-red-600">{activeCount} Inisiatif</div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">Kempen Aktif</div>
            </div>
          </div>

          {/* 3. Featured Campaign Spotlight */}
          {featuredCampaign && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Inisiatif Terkini</span>
              </div>
              <DonationCard
                campaign={featuredCampaign}
                featured={true}
                onDonate={handleOpenDetail}
                onShare={handleShare}
                onEdit={handleOpenEdit}
                isManagement={isManagement}
              />
            </div>
          )}

          {/* 4. Filters & Search - Floating Pills on Pure White Canvas */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 pt-2">
            {/* Category Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 custom-scrollbar text-xs font-bold">
              {[
                'Semua',
                'Pembangunan Gelanggang',
                'Kebajikan & Asnaf',
                'Peralatan & Busana',
                'Kontinjen & Sukan',
                'Wakaf Am'
              ].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full transition-all whitespace-nowrap cursor-pointer ${selectedCategory === cat
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100/80 hover:bg-slate-200/60 text-slate-700'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search Bar - Sleek Rounded-Full Input */}
            <div className="relative min-w-[240px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cari kempen infaq..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50/80 border border-slate-200/80 rounded-full text-xs font-medium focus:bg-white focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-hidden transition"
              />
            </div>
          </div>

          {/* All Campaigns Full Width Grid */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-extrabold text-base text-slate-900">
                Semua Inisiatif Tabung ({filteredCampaigns.length})
              </h3>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="p-12 bg-white rounded-3xl border border-slate-200 text-center space-y-3">
                <Heart className="w-8 h-8 text-slate-300 mx-auto" />
                <div className="text-sm font-bold text-slate-700">Tiada Kempen Ditemui</div>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Tiada kempen infaq yang sepadan dengan tapisan kategori atau carian anda.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredCampaigns.map((camp) => (
                  <DonationCard
                    key={camp.id}
                    campaign={camp}
                    onDonate={handleOpenDetail}
                    onShare={handleShare}
                    onEdit={handleOpenEdit}
                    isManagement={isManagement}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};


